//From https://betterprogramming.pub/how-to-handle-errors-of-angular-reactive-forms-with-three-lines-of-code-3caaa1eb8324

import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Injectable } from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CurrencyMaskingPattern, CurrencyMaskingReadablePattern } from 'src/app/system/utility/currency-utility.service';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';

export declare interface ServerError {
    [key: string]: any[];
}

@Injectable({
    providedIn: 'root'
})
export class ErrorHandler {
    private form!: FormGroup;
    private errorObject!: ServerError;
    private patternErrorMapping: Map<string, string> = new Map<string, string>();

    constructor(
        private translate: TranslateUtilityService
    ) {
        this.patternErrorMapping.set(PatternValidationType.ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_REG_EXP.toString(), PatternValidationType.ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_ALPHANUMERIC_SPECIAL_CHAR_2_REG_EXP.toString(), PatternValidationType.ALLOW_ALPHANUMERIC_SPECIAL_CHAR_2_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_ALPHANUMERIC_SPECIAL_CHAR_3_REG_EXP.toString(), PatternValidationType.ALLOW_ALPHANUMERIC_SPECIAL_CHAR_3_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_ALPHANUMERIC_SPACE_DOLLAR_REG_EXP.toString(), PatternValidationType.ALLOW_ALPHANUMERIC_SPACE_DOLLAR_ERR_MSG);

        this.patternErrorMapping.set(PatternValidationType.ALLOW_1_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_1_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_2_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_2_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_3_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_3_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_4_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_4_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_5_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_5_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_6_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_6_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_7_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_7_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_8_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_8_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_9_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_9_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_10_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_10_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_11_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_11_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_12_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_12_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_13_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_13_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_14_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_14_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_15_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_15_DIGIT_ERR_MSG);
        this.patternErrorMapping.set(PatternValidationType.ALLOW_16_DIGIT_REG_EXP.toString(), PatternValidationType.ALLOW_16_DIGIT_ERR_MSG);
    }

    private static hasError(control: AbstractControl): boolean {
        return control.invalid && (control.dirty || control.touched);
    }


    /**
     * Takes server error obj and set errors to appropriate fields at form given.
     *
     * @param serverError       Error object that is received from the server
     */
    public organizeServerErrors(serverError: ServerError) {
        if (serverError && typeof serverError === 'object') {
            this.setErrorToFormFields(serverError);
            this.findErrors(this.form.controls);
        }
    }

    /**
     * Listen's for invalid status of the form given and find's it's errors.
     *
     * @param form              Form to be listened
     * @param errorObject       Error object which to set errors.
     */
    public handleErrors(form: FormGroup, errorObject: any) {
        this.form = form;
        this.errorObject = errorObject;

        form.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
        ).subscribe(() => {
            if (form.invalid) {
                this.findErrors(form.controls);
            }
        });
    }

    /** Used to call when an invalid form has been submit
     *  By setting error we emit a status changed event for the form.
     */
    public invalidFormHasSubmit() {
        this.form?.markAllAsTouched();
        this.form?.setErrors({});
    }

    /**
     * Finds errors of the form given and set's them to errorObject.
     */
    public findAllErrors(): any {
        this.form.markAsDirty();
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.findErrors(this.form.controls);
        }
    }


    /**
     * Reset the Form to default value and clear all errors.
     */
    public resetForm(): void {
        this.form.reset();
        this.form.markAsPristine();
        this.form.markAsUntouched();
        Object.keys(this.form.controls).forEach(key => {
          this.form.controls[key].setErrors(null)
        });
    }


    /** Finds appropriate fields on form and set's the server error. */
    private setErrorToFormFields(serverError: ServerError) {
        Object.keys(serverError).forEach(field => {
            if (!Array.isArray(serverError[field])) {
                this.setErrorsToNestedFields(field, serverError);
            } else {
                const errorMessages: any[] = serverError[field];
                this.form.get(field)?.setErrors({ serverError: errorMessages[0].message });
            }
        });
    }

    /**
     * Takes nested field name and set's server error to appropriate nested field.
     * @param nestedFieldName       Usually formControlName of a FormArray.
     * @param serverError           Error object that is received from the server
     */
    private setErrorsToNestedFields(nestedFieldName: string, serverError: any) {
        const nestedFormErrors = serverError[nestedFieldName];
        Object.keys(nestedFormErrors).forEach(field => {
            const formControl = this.form.get(field);
            if (formControl) {
                formControl.setErrors({ serverError: nestedFormErrors[field] });
            } else {
                const nestedForm = this.form.get(nestedFieldName);
                const nestedField = nestedForm!.get(field);
                if (nestedField) {
                    nestedField.setErrors({ serverError: nestedFormErrors[field] });
                }
            }
        });
    }

    /**
     * Find which control contains the error and set required { control -> error message } combination
     * into the errorObject given previously.
     *
     * @param controls      Abstract Controls of the form which contains errors.
     */
    private findErrors(controls: { [key: string]: AbstractControl }) {
        Object.keys(controls).forEach((control: string) => {
            if (controls[control] instanceof FormArray) {
                Object.defineProperty(this.errorObject, control, { value: [], writable: true });
                this.findErrorsOnFormArrays(controls[control] as FormArray, control);

            } else if (controls[control] instanceof FormControl) {
                this.findErrorsOnFormControls(controls, control);
            }
        });
    }

    private findErrorsOnFormArrays(formArray: FormArray, formArrayName: string) {
        let i = 0;
        for (const formGroup of formArray.controls as FormGroup[]) {
            const controls = formGroup.controls;
            const formArrayErrors: any[] = this.errorObject[formArrayName];
            formArrayErrors.push({});
            Object.keys(controls).forEach(control => {
                if (ErrorHandler.hasError(controls[control])) {
                    let message = this.getErrorMessage(controls[control].errors!, controls[control]);
                    Object.defineProperty(formArrayErrors[i], control, { value: message, writable: true });
                }
            });
            i++;
        }
    }

    private findErrorsOnFormControls(controls: { [key: string]: AbstractControl }, control: string) {
        if (ErrorHandler.hasError(controls[control])) {
            let message = this.getErrorMessage(controls[control].errors!, controls[control]);
            this.setErrorToErrorObject(control, message);
        }
    }

    /**
     * Find's error type and set's a message value for this type.
     *
     * @param errors        Validation Error obj.
     */
    private getErrorMessage(errors: ValidationErrors, control: AbstractControl): string {

        if (errors['serverError']) {
            return errors['serverError'];
        } else if (errors['required']) {
            return 'Required field';
        } else if (errors['minlength']) {
            return `Minimum length: ${errors['minlength'].requiredLength} characters`;
        } else if (errors['maxlength']) {
            return `Maximum length: ${errors['maxlength'].requiredLength} characters`;
        } else if (errors['email']) {
            return 'Email is not valid';
        } else if (errors['min']) {
            return `Minimum is ${errors['min'].min}`;
        } else if (errors['max']) {
            return `Maximum value is ${errors['max'].max}`;
        } else if (errors['pattern']) {
            let errorMessage = this.lookupCommonPattern(errors['pattern'].requiredPattern, control);
            return errorMessage;
        } else if (errors['passwordMismatch']) {
            return 'Passwords do not match';
        } else {
            return '';
        }
    }

    /**
     * Set's a new property to errorObject with key from the field's name and error message as a value.
     * @param field         Field which contains error.
     * @param message       Error message.
     */
    public setErrorToErrorObject(field: string, message: string) {
        if (this.errorObject && message) {
            Object.defineProperty(this.errorObject, field, { value: message, writable: true });
        }
    }

    /**
     * Best effor to "translate" common regular expression pattern into human readable format
     */
    private lookupCommonPattern(pattern: string, _control: AbstractControl) {

        let errorMessage = this.patternErrorMapping.get(pattern);
        if (errorMessage) {
            return this.translate.instant(errorMessage);
        }

        switch (pattern) {
            case CurrencyMaskingPattern.DECIMAL_POINT:
                return CurrencyMaskingReadablePattern.DECIMAL_POINT;
            case CurrencyMaskingPattern.DECIMAL_COMMA:
                return CurrencyMaskingReadablePattern.DECIMAL_COMMA;
            default:
                return 'Invalid pattern';
        }
    }

}


export class PatternValidationType {
    public static readonly ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_REG_EMOJI_EXP = /^[\u00a9\u00ae\u2000-\u3300\ud83c\ud000-\udfff\ud83d\ud000-\udfff\ud83e\ud000-\udfffA-Za-z0-9 \.,\-'"/\\?&%#_@!$\n]*$/
    public static readonly ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_REG_EMOJI_ERR_MSG = "ERROR_PATTERN.ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_REG_EMOJI_ERR_MSG";
    public static readonly ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_REG_EXP = /^[A-Za-z0-9 \.,\-'"?&%#@!\n]*$/;
    public static readonly ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_ERR_MSG = "ERROR_PATTERN.ALLOW_ALPHANUMERIC_SPACE_SPECIAL_CHARACTER_1_ERR_MSG";
    public static readonly ALLOW_ALPHANUMERIC_SPECIAL_CHAR_2_REG_EXP = /^[a-zA-Z0-9 .'-]*$/;
    public static readonly ALLOW_ALPHANUMERIC_SPECIAL_CHAR_2_ERR_MSG = "ERROR_PATTERN.ALLOW_ALPHANUMERIC_SPECIAL_CHAR_2_ERR_MSG";
    public static readonly ALLOW_ALPHANUMERIC_SPACE_DOLLAR_REG_EXP = /^[A-Za-z0-9 $.]+$/;
    public static readonly ALLOW_ALPHANUMERIC_SPACE_DOLLAR_ERR_MSG = "ERROR_PATTERN.ALLOW_ALPHANUMERIC_SPACE_DOLLAR_ERR_MSG";
    public static readonly ALLOW_ALPHANUMERIC_SPECIAL_CHAR_3_REG_EXP = /^[a-zA-Z0-9 ;=#+*'%^!$.{}|:()/_?,@\r\n-]*$/;
    public static readonly ALLOW_ALPHANUMERIC_SPECIAL_CHAR_3_ERR_MSG = "ERROR_PATTERN.ALLOW_ALPHANUMERIC_SPECIAL_CHAR_3_ERR_MSG";
    public static readonly ALLOW_1_DIGIT_REG_EXP = /^[0-9]{1,1}$/;
    public static readonly ALLOW_1_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_1_DIGIT_ERR_MSG";
    public static readonly ALLOW_2_DIGIT_REG_EXP = /^[0-9]{1,2}$/;
    public static readonly ALLOW_2_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_2_DIGIT_ERR_MSG";
    public static readonly ALLOW_3_DIGIT_REG_EXP = /^[0-9]{1,3}$/;
    public static readonly ALLOW_3_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_3_DIGIT_ERR_MSG";
    public static readonly ALLOW_4_DIGIT_REG_EXP = /^[0-9]{1,4}$/;
    public static readonly ALLOW_4_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_4_DIGIT_ERR_MSG";
    public static readonly ALLOW_5_DIGIT_REG_EXP = /^[0-9]{1,5}$/;
    public static readonly ALLOW_5_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_5_DIGIT_ERR_MSG";
    public static readonly ALLOW_6_DIGIT_REG_EXP = /^[0-9]{1,6}$/;
    public static readonly ALLOW_6_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_6_DIGIT_ERR_MSG";
    public static readonly ALLOW_7_DIGIT_REG_EXP = /^[0-9]{1,7}$/;
    public static readonly ALLOW_7_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_7_DIGIT_ERR_MSG"
    public static readonly ALLOW_8_DIGIT_REG_EXP = /^[0-9]{1,8}$/;
    public static readonly ALLOW_8_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_8_DIGIT_ERR_MSG";
    public static readonly ALLOW_9_DIGIT_REG_EXP = /^[0-9]{1,9}$/;
    public static readonly ALLOW_9_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_9_DIGIT_ERR_MSG";
    public static readonly ALLOW_10_DIGIT_REG_EXP = /^[0-9]{1,10}$/;
    public static readonly ALLOW_10_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_10_DIGIT_ERR_MSG";
    public static readonly ALLOW_11_DIGIT_REG_EXP = /^[0-9]{1,11}$/;
    public static readonly ALLOW_11_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_11_DIGIT_ERR_MSG";
    public static readonly ALLOW_12_DIGIT_REG_EXP = /^[0-9]{1,12}$/;
    public static readonly ALLOW_12_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_12_DIGIT_ERR_MSG";
    public static readonly ALLOW_13_DIGIT_REG_EXP = /^[0-9]{1,13}$/;
    public static readonly ALLOW_13_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_13_DIGIT_ERR_MSG";
    public static readonly ALLOW_14_DIGIT_REG_EXP = /^[0-9]{1,14}$/;
    public static readonly ALLOW_14_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_14_DIGIT_ERR_MSG";
    public static readonly ALLOW_15_DIGIT_REG_EXP = /^[0-9]{1,15}$/;
    public static readonly ALLOW_15_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_15_DIGIT_ERR_MSG";
    public static readonly ALLOW_16_DIGIT_REG_EXP = /^[0-9]{1,16}$/;
    public static readonly ALLOW_16_DIGIT_ERR_MSG = "ERROR_PATTERN.MAXIMUM_16_DIGIT_ERR_MSG";

}